import { createListenerMiddleware } from '@reduxjs/toolkit'
import { LOCATION_CHANGE } from 'lib/routing/appRouting'
import { SET_FREE_PREVIEW_AUTO_ENROL } from 'actions/actionConstants'
import { getFreePreviewAutoEnrolSetting } from 'api/promo'
import { getCookies, setCookie } from 'lib/web/cookieUtils'
import config from 'constants/config'

// Client-side fallback for resolving Free Preview auto-enrolment eligibility on SPA navigations
// where SSR didn't seed it. Eligibility is persisted in the `fp_auto_enrol` cookie for 7 days so a
// return visit still enrols on signup. Mirrors bypassRegionMiddleware.
const COOKIE_KEY = 'fp_auto_enrol'
// Eligibility persists until the rule's end_datetime, capped at 7 days from the matching visit.
const MAX_COOKIE_TTL_SECONDS = 7 * 24 * 60 * 60

const cookieMaxAgeSeconds = (endDatetime?: string | null): number => {
  if (!endDatetime) return MAX_COOKIE_TTL_SECONDS
  const remaining = Math.floor((new Date(endDatetime).getTime() - Date.now()) / 1000)
  return Math.max(0, Math.min(remaining, MAX_COOKIE_TTL_SECONDS))
}

const freePreviewAutoEnrolMiddleware = createListenerMiddleware()

freePreviewAutoEnrolMiddleware.startListening({
  type: LOCATION_CHANGE,
  effect: async(_action, listenerApi) => {
    // Gate behind the feature flag (off in prod) so we don't hit svc-promo on every page load.
    // Mirrors the SSR gate on fetchFreePreviewAutoEnrolSetting.
    if (!config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW) {
      listenerApi.unsubscribe()
      return
    }

    const state = listenerApi.getState() as App.State
    if (state.marketing.freePreviewAutoEnrol) {
      listenerApi.unsubscribe()
      return
    }

    const cookies = getCookies()
    const savedSettingId = cookies[COOKIE_KEY]
    if (savedSettingId) {
      listenerApi.dispatch({ type: SET_FREE_PREVIEW_AUTO_ENROL, enabled: true, settingId: savedSettingId })
      listenerApi.unsubscribe()
      return
    }

    listenerApi.unsubscribe()

    const { source, medium, campaign, term } = state.utm
    const region = state.geo.currentRegionCode

    try {
      const result = await getFreePreviewAutoEnrolSetting({
        region: region || undefined,
        source: source || undefined,
        medium: medium || undefined,
        campaign: campaign || undefined,
        term: term || undefined,
      })

      listenerApi.dispatch({
        type: SET_FREE_PREVIEW_AUTO_ENROL,
        enabled: result.enabled,
        settingId: result.setting_id ?? null,
      })

      if (result.enabled) {
        setCookie(COOKIE_KEY, result.setting_id ?? '1', cookieMaxAgeSeconds(result.end_datetime), '/', 'Lax', document.domain)
      }
    } catch {
      // non-critical — freePreviewAutoEnrol stays unset
    }
  },
})

export default freePreviewAutoEnrolMiddleware
